import React, {
  Suspense,
  lazy,
  memo,
  useEffect,
  useState,
  LazyExoticComponent,
  FC,
} from "react";

import { ChildrenProp } from "../../types";
import { getIsInStandaloneMode } from "../../services/env";
import { useDynamicScript, loadModule } from "./helpers";
import {
  CreateFederatedComponentConsumerParams,
  FederatedComponentConsumerProps,
  WithFederatedHookParams,
} from "./types";
import { Loading } from "./components/Loading";
import { Failed } from "./components/Failed";

export const createFederatedComponentConsumer = <FederatedComponentProps,>({
  remoteEntryUrl,
  packageName,
  componentPath,
  componentDisplayName,
}: CreateFederatedComponentConsumerParams): FC<
  FederatedComponentConsumerProps & FederatedComponentProps & ChildrenProp
> => {
  const displayNameForFederatedComponentConsumer = `FederatedComponentConsumerFor${componentDisplayName}`;

  const FederatedComponentConsumerForStandaloneMode: FC<
    FederatedComponentConsumerProps & FederatedComponentProps & ChildrenProp
  > = ({ children }) => (
    <div data-qa={displayNameForFederatedComponentConsumer}>{children}</div>
  );
  FederatedComponentConsumerForStandaloneMode.displayName =
    displayNameForFederatedComponentConsumer;
  if (getIsInStandaloneMode()) {
    return FederatedComponentConsumerForStandaloneMode;
  }

  const FederatedComponentConsumer: FC<
    FederatedComponentConsumerProps & FederatedComponentProps & ChildrenProp
  > = memo((federatedComponentProps) => {
    const {
      // IMPORTANT: Be sure to extract all items of FederatedComponentConsumerProps here
      // so that "rest" does not contain any items of FederatedComponentConsumerProps.
      federatedComponentFailedElement,
      federatedComponentLoadingElement,
      ...rest
    } = federatedComponentProps;

    const { isReady, itFailed } = useDynamicScript(remoteEntryUrl);
    const [Component, setComponent] =
      useState<LazyExoticComponent<React.ComponentType<any>>>();

    useEffect(() => {
      if (!itFailed && isReady) {
        setComponent(() => lazy(loadModule(packageName, componentPath)));
      }
    }, [itFailed, isReady]);

    if (itFailed) {
      return federatedComponentFailedElement || <Failed />;
    }

    if (!Component) {
      return federatedComponentLoadingElement || <Loading />;
    }

    return (
      <Suspense fallback={federatedComponentLoadingElement || <Loading />}>
        <Component {...rest} />
      </Suspense>
    );
  });

  FederatedComponentConsumer.displayName =
    displayNameForFederatedComponentConsumer;

  return FederatedComponentConsumer;
};

export const withFederatedHook = <ComponentProps,>({
  remoteEntryUrl,
  packageName,
  federatedHooks,
  Component,
}: WithFederatedHookParams): React.FC<
  FederatedComponentConsumerProps & ComponentProps & ChildrenProp
> => {
  const ComponentWithHooks: FC<
    FederatedComponentConsumerProps & ComponentProps & ChildrenProp
  > = memo((props) => {
    const {
      // IMPORTANT: Be sure to extract all items of FederatedComponentConsumerProps here
      // so that "rest" does not contain any items of FederatedComponentConsumerProps.
      federatedComponentFailedElement,
      federatedComponentLoadingElement,
      ...rest
    } = props;

    const { isReady, itFailed } = useDynamicScript(remoteEntryUrl);
    const [moduleFailed, setModuleFailed] = useState(false);
    const [hooks, setHooks] = useState<{ [hookPropName: string]: any }>({});

    useEffect(() => {
      // Logic to avoid anything other than hooks
      if (
        federatedHooks
          .map((h) => {
            const paths = h.hookPath.split("/");
            return paths[paths.length - 1];
          })
          .some((h) => h.startsWith("use") === false)
      ) {
        throw new Error("You can only import hooks!");
      }
      if (isReady && !itFailed) {
        federatedHooks.forEach((h) => {
          loadModule(packageName, h.hookPath)()
            .then((module) => {
              if (module.default) {
                setHooks((hooks) => ({
                  ...hooks,
                  [h.hookPropName]: module.default,
                }));
              } else {
                setModuleFailed(true);
              }
            })
            .catch(() => {
              setModuleFailed(true);
            });
        });
      }
    }, [isReady, itFailed]);

    if (itFailed || moduleFailed) {
      return federatedComponentFailedElement || <Failed />;
    }

    if (!isReady || Object.keys(hooks).length !== federatedHooks.length) {
      return federatedComponentLoadingElement || <Loading />;
    }

    return <Component {...{ ...rest, ...hooks }} />;
  });
  ComponentWithHooks.displayName = Component.displayName;
  return ComponentWithHooks;
};
